import React, { useState, useEffect } from 'react';
import {
  Alert,
  Button,
  Box,
  TextField,
  Paper,
  Typography,
  Stack,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Update as UpdateIcon
} from '@mui/icons-material';
import { USFlag, EUFlag } from '../../components/Flags';
import { config } from '../../config';
import { useIsMobile } from '../../hooks/useResponsive';
import './BusinessSettings/BusinessSettings.css';

interface CurrencyRate {
  usd_rate: number;
  eur_rate: number;
  last_updated?: string;
}

const CurrencySettings: React.FC = () => {
  const isMobile = useIsMobile();
  const [rates, setRates] = useState<CurrencyRate>({ usd_rate: 0, eur_rate: 0 });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ usd: boolean; eur: boolean }>({ usd: false, eur: false });
  const [editValues, setEditValues] = useState({ usd: '', eur: '' });

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem(config.auth.tokenKey);
    console.log('[CurrencySettings] Token check:', token ? 'exists' : 'missing');
    console.log('[CurrencySettings] Token key:', config.auth.tokenKey);
    console.log('[CurrencySettings] Token length:', token?.length || 0);
    
    if (!token) {
      console.error('[CurrencySettings] No token found in localStorage');
      throw new Error('Токен авторизації відсутній. Увійдіть в систему знову.');
    }
    
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
    
    console.log('[CurrencySettings] Headers prepared:', {
      'Content-Type': headers['Content-Type'],
      'Authorization': headers['Authorization'] ? 'Bearer [TOKEN]' : 'missing'
    });
    
    return headers;
  };

  // Завантаження курсів валют
  useEffect(() => {
    console.log('[CurrencySettings] Component mounted, checking auth state');
    
    // Check token on mount
    const token = localStorage.getItem(config.auth.tokenKey);
    console.log('[CurrencySettings] Initial token check:', token ? 'exists' : 'missing');
    console.log('[CurrencySettings] Using token key:', config.auth.tokenKey);
    
    fetchCurrencyRates();
  }, []);

  const fetchCurrencyRates = async () => {
    try {
      console.log('[CurrencySettings] Fetching currency rates (public endpoints)');
      setLoading(true);
      setError(null);
      
      // Отримуємо обидва курси
      const [usdResponse, eurResponse] = await Promise.all([
        fetch('/api/v1/currency/usd-rate'),
        fetch('/api/v1/currency/eur-rate')
      ]);

      console.log(`[CurrencySettings] Public USD response: ${usdResponse.status}, ok: ${usdResponse.ok}`);
      console.log(`[CurrencySettings] Public EUR response: ${eurResponse.status}, ok: ${eurResponse.ok}`);

      if (!usdResponse.ok || !eurResponse.ok) {
        const usdError = usdResponse.ok ? '' : await usdResponse.text();
        const eurError = eurResponse.ok ? '' : await eurResponse.text();
        console.error('[CurrencySettings] Public endpoint errors:', { usdError, eurError });
        throw new Error('Не вдалося завантажити курси валют');
      }

      const usdData = await usdResponse.json();
      const eurData = await eurResponse.json();
      
      console.log('[CurrencySettings] Public rates loaded:', { usdData, eurData });

      setRates({
        usd_rate: usdData.usd_rate || 0,
        eur_rate: eurData.eur_rate || 0,
        last_updated: new Date().toISOString()
      });
    } catch (err) {
      console.error('[CurrencySettings] Error fetching rates:', err);
      setError(err instanceof Error ? err.message : 'Помилка завантаження курсів');
    } finally {
      setLoading(false);
    }
  };

  const updateFromAPI = async (currency: 'usd' | 'eur') => {
    try {
      console.log(`[CurrencySettings] Starting ${currency} update from API`);
      setUpdating(currency);
      setError(null);
      setSuccess(null);

      const url = `/api/v1/admin/currency/${currency}-rate/update-from-api`;
      console.log(`[CurrencySettings] Request URL: ${url}`);
      
      const headers = getAuthHeaders();
      console.log(`[CurrencySettings] Making POST request to ${url}`);

      const response = await fetch(url, {
        method: 'POST',
        headers
      });

      console.log(`[CurrencySettings] Response status: ${response.status}`);
      console.log(`[CurrencySettings] Response ok: ${response.ok}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[CurrencySettings] Error response:`, errorText);
        
        if (response.status === 401) {
          console.error('[CurrencySettings] 401 Unauthorized - token invalid or expired');
          throw new Error('Помилка авторизації. Увійдіть в систему знову.');
        }
        throw new Error(`Не вдалося оновити курс ${currency.toUpperCase()}. Статус: ${response.status}`);
      }

      const data = await response.json();
      console.log(`[CurrencySettings] Success response:`, data);
      setSuccess(data.message);
      await fetchCurrencyRates(); // Оновити дані
    } catch (err) {
      console.error(`[CurrencySettings] Error updating ${currency}:`, err);
      setError(err instanceof Error ? err.message : `Помилка оновлення курсу ${currency.toUpperCase()}`);
    } finally {
      setUpdating(null);
    }
  };

  const updateAllFromAPI = async () => {
    try {
      console.log('[CurrencySettings] Starting batch update from API');
      setUpdating('all');
      setError(null);
      setSuccess(null);

      const usdUrl = '/api/v1/admin/currency/usd-rate/update-from-api';
      const eurUrl = '/api/v1/admin/currency/eur-rate/update-from-api';
      
      console.log(`[CurrencySettings] Making parallel requests: ${usdUrl}, ${eurUrl}`);
      
      const headers = getAuthHeaders();

      const [usdResponse, eurResponse] = await Promise.all([
        fetch(usdUrl, {
          method: 'POST',
          headers
        }),
        fetch(eurUrl, {
          method: 'POST',
          headers
        })
      ]);

      console.log(`[CurrencySettings] USD response status: ${usdResponse.status}, ok: ${usdResponse.ok}`);
      console.log(`[CurrencySettings] EUR response status: ${eurResponse.status}, ok: ${eurResponse.ok}`);

      if (!usdResponse.ok || !eurResponse.ok) {
        const usdError = usdResponse.ok ? '' : await usdResponse.text();
        const eurError = eurResponse.ok ? '' : await eurResponse.text();
        
        console.error('[CurrencySettings] Batch update errors:', { usdError, eurError });
        
        if (usdResponse.status === 401 || eurResponse.status === 401) {
          console.error('[CurrencySettings] 401 Unauthorized in batch update');
          throw new Error('Помилка авторизації. Увійдіть в систему знову.');
        }
        throw new Error('Не вдалося оновити курси валют');
      }

      const [usdData, eurData] = await Promise.all([
        usdResponse.json(),
        eurResponse.json()
      ]);

      console.log('[CurrencySettings] Batch update success:', { usdData, eurData });
      setSuccess('Курси валют успішно оновлено з API ПриватБанку');
      await fetchCurrencyRates();
    } catch (err) {
      console.error('[CurrencySettings] Batch update error:', err);
      setError(err instanceof Error ? err.message : 'Помилка оновлення курсів');
    } finally {
      setUpdating(null);
    }
  };

  const startEdit = (currency: 'usd' | 'eur') => {
    setEditing({ ...editing, [currency]: true });
    const rateKey = `${currency}_rate` as keyof CurrencyRate;
    setEditValues({
      ...editValues,
      [currency]: rates[rateKey]?.toString() || ''
    });
  };

  const cancelEdit = (currency: 'usd' | 'eur') => {
    setEditing({ ...editing, [currency]: false });
    setEditValues({ ...editValues, [currency]: '' });
  };

  const saveEdit = async (currency: 'usd' | 'eur') => {
    try {
      setUpdating(currency);
      setError(null);
      setSuccess(null);

      const rate = parseFloat(editValues[currency]);
      if (isNaN(rate) || rate <= 0) {
        setError('Введіть коректне значення курсу');
        return;
      }

      const response = await fetch(`/api/v1/admin/currency/${currency}-rate`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ rate })
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Помилка авторизації. Увійдіть в систему знову.');
        }
        throw new Error(`Не вдалося оновити курс ${currency.toUpperCase()}`);
      }

      const data = await response.json();
      setSuccess(data.message);
      setEditing({ ...editing, [currency]: false });
      await fetchCurrencyRates();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Помилка оновлення курсу ${currency.toUpperCase()}`);
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="settings-container">
      <Typography variant="h4" gutterBottom>
        Курси валют
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Кнопка оновлення всіх курсів */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">Автоматичне оновлення</Typography>
            <Typography variant="body2" color="text.secondary">
              Оновлення курсів з API ПриватБанку (курс продажу)
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={updating === 'all' ? <CircularProgress size={20} /> : <UpdateIcon />}
            onClick={updateAllFromAPI}
            disabled={updating !== null}
          >
            {isMobile ? '' : (updating === 'all' ? 'Оновлення...' : 'Оновити всі курси')}
          </Button>
        </Stack>
      </Paper>

      {/* Карточки курсів валют */}
      <Grid container spacing={3}>
        {/* USD */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  USD
                  <USFlag />
                </Typography>
                <Chip 
                  label="Долар США" 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                />
              </Stack>

              {editing.usd ? (
                <Stack spacing={2}>
                  <TextField
                    label="Курс (UAH)"
                    type="number"
                    value={editValues.usd}
                    onChange={(e) => setEditValues({ ...editValues, usd: e.target.value })}
                    fullWidth
                    inputProps={{ step: "0.01", min: "0" }}
                  />
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      startIcon={updating === 'usd' ? <CircularProgress size={16} /> : <SaveIcon />}
                      onClick={() => saveEdit('usd')}
                      disabled={updating !== null}
                      size="small"
                    >
                      {isMobile ? '' : 'Зберегти'}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={() => cancelEdit('usd')}
                      disabled={updating !== null}
                      size="small"
                    >
                      {isMobile ? '' : 'Скасувати'}
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                <Stack spacing={2}>
                  <Typography variant="h4" color="primary">
                    {rates.usd_rate.toFixed(2)} ₴
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Курс продажу ПриватБанку
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Оновити з API">
                      <IconButton
                        onClick={() => updateFromAPI('usd')}
                        disabled={updating !== null}
                        size="small"
                      >
                        {updating === 'usd' ? <CircularProgress size={20} /> : <RefreshIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Редагувати">
                      <IconButton
                        onClick={() => startEdit('usd')}
                        disabled={updating !== null}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* EUR */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  EUR
                  <EUFlag />
                </Typography>
                <Chip 
                  label="Євро" 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                />
              </Stack>

              {editing.eur ? (
                <Stack spacing={2}>
                  <TextField
                    label="Курс (UAH)"
                    type="number"
                    value={editValues.eur}
                    onChange={(e) => setEditValues({ ...editValues, eur: e.target.value })}
                    fullWidth
                    inputProps={{ step: "0.01", min: "0" }}
                  />
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      startIcon={updating === 'eur' ? <CircularProgress size={16} /> : <SaveIcon />}
                      onClick={() => saveEdit('eur')}
                      disabled={updating !== null}
                      size="small"
                    >
                      {isMobile ? '' : 'Зберегти'}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={() => cancelEdit('eur')}
                      disabled={updating !== null}
                      size="small"
                    >
                      {isMobile ? '' : 'Скасувати'}
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                <Stack spacing={2}>
                  <Typography variant="h4" color="primary">
                    {rates.eur_rate.toFixed(2)} ₴
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Курс продажу ПриватБанку
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Оновити з API">
                      <IconButton
                        onClick={() => updateFromAPI('eur')}
                        disabled={updating !== null}
                        size="small"
                      >
                        {updating === 'eur' ? <CircularProgress size={20} /> : <RefreshIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Редагувати">
                      <IconButton
                        onClick={() => startEdit('eur')}
                        disabled={updating !== null}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Інформація про автоматичне оновлення */}
      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Інформація про автоматичне оновлення
        </Typography>
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            • Курси валют автоматично оновлюються з API ПриватБанку
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Оновлення відбувається щодня о 09:30 та 15:00
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Використовується курс продажу для обох валют
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Адміністратор може вручну оновити курси в будь-який час
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
};

export default CurrencySettings;
